import { useState, useEffect, useCallback } from 'react';
import { DataTable, Button, Modal } from '@sundkortid/ui';
import { PASS_LABELS_IS, formatIsk } from '@sundkortid/shared';
import type { PaginationMeta, PassType, PassStatus } from '@sundkortid/shared';
import { api } from '../../api/client';
import { AdminNav } from './AdminDashboardPage';

interface AdminPass {
  id: string;
  passType: PassType;
  status: PassStatus;
  totalEntries: number | null;
  usedEntries: number;
  validFrom: string;
  validUntil: string | null;
  pricePaidIsk: number;
  createdAt: string;
  userName: string | null;
  userEmail: string | null;
}

const STATUS_LABELS: Record<PassStatus, string> = {
  active: 'Virkt',
  expired: 'Útrunnið',
  fully_redeemed: 'Fullnýtt',
  revoked: 'Afturkallað',
};

const STATUS_COLORS: Record<PassStatus, string> = {
  active: 'bg-green-100 text-green-800',
  expired: 'bg-gray-100 text-gray-800',
  fully_redeemed: 'bg-yellow-100 text-yellow-800',
  revoked: 'bg-red-100 text-red-800',
};

export function AdminPassesPage() {
  const [passes, setPasses] = useState<AdminPass[]>([]);
  const [meta, setMeta] = useState<PaginationMeta | null>(null);
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [revokeTarget, setRevokeTarget] = useState<AdminPass | null>(null);
  const [actionLoading, setActionLoading] = useState(false);

  const fetchPasses = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(page), perPage: '20' });
      if (statusFilter) params.set('status', statusFilter);
      if (typeFilter) params.set('type', typeFilter);

      const result = await api.getWithMeta<AdminPass[]>(`/admin/passes?${params}`);
      setPasses(result.data);
      setMeta(result.meta ?? null);
    } catch {
    } finally {
      setLoading(false);
    }
  }, [page, statusFilter, typeFilter]);

  useEffect(() => { fetchPasses(); }, [fetchPasses]);
  useEffect(() => { setPage(1); }, [statusFilter, typeFilter]);

  const handleRevoke = async () => {
    if (!revokeTarget) return;
    setActionLoading(true);
    try {
      await api.patch(`/admin/passes/${revokeTarget.id}/revoke`);
      setRevokeTarget(null);
      fetchPasses();
    } catch {
    } finally {
      setActionLoading(false);
    }
  };

  const columns = [
    {
      key: 'passType',
      header: 'Tegund',
      render: (row: AdminPass) => PASS_LABELS_IS[row.passType],
    },
    {
      key: 'status',
      header: 'Staða',
      render: (row: AdminPass) => (
        <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${STATUS_COLORS[row.status]}`}>
          {STATUS_LABELS[row.status]}
        </span>
      ),
    },
    {
      key: 'userName',
      header: 'Notandi',
      render: (row: AdminPass) => row.userName ?? row.userEmail ?? '—',
    },
    {
      key: 'usage',
      header: 'Notkun',
      render: (row: AdminPass) =>
        row.totalEntries != null ? `${row.usedEntries} / ${row.totalEntries}` : `${row.usedEntries} (ótakmarkað)`,
    },
    {
      key: 'pricePaidIsk',
      header: 'Verð',
      render: (row: AdminPass) => formatIsk(row.pricePaidIsk),
    },
    {
      key: 'validUntil',
      header: 'Gildir til',
      render: (row: AdminPass) =>
        row.validUntil ? new Date(row.validUntil).toLocaleDateString('is-IS') : '—',
    },
    {
      key: 'actions',
      header: 'Aðgerðir',
      render: (row: AdminPass) =>
        row.status === 'active' ? (
          <Button size="sm" variant="danger" onClick={() => setRevokeTarget(row)}>
            Afturkalla
          </Button>
        ) : null,
    },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white border-b border-gray-100 px-6 py-4">
        <div className="max-w-6xl mx-auto flex flex-col gap-4">
          <h1 className="text-xl font-title font-bold text-primary">Sundkortið — Stjórnborð</h1>
          <AdminNav />
        </div>
      </header>
      <main className="max-w-6xl mx-auto px-6 py-8">
        <h2 className="text-2xl font-title font-bold text-primary mb-6">Kort</h2>

        <div className="flex flex-wrap gap-3 mb-4">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
          >
            <option value="">Öll stöður</option>
            <option value="active">Virk</option>
            <option value="expired">Útrunnin</option>
            <option value="fully_redeemed">Fullnýtt</option>
            <option value="revoked">Afturkölluð</option>
          </select>
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
          >
            <option value="">Allar tegundir</option>
            <option value="single">Stakur miði</option>
            <option value="ten_pack">10 miða kort</option>
            <option value="monthly">Mánaðarkort</option>
            <option value="annual">Árskort</option>
          </select>
        </div>

        {loading ? (
          <p className="text-gray-500">Hleð gögnum...</p>
        ) : (
          <>
            <DataTable columns={columns} data={passes} keyExtractor={(row) => row.id} />
            {meta && meta.totalPages > 1 && (
              <div className="flex items-center justify-between mt-4">
                <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage(page - 1)}>
                  Fyrri
                </Button>
                <span className="text-sm text-gray-600">Síða {meta.page} af {meta.totalPages}</span>
                <Button size="sm" variant="outline" disabled={page >= meta.totalPages} onClick={() => setPage(page + 1)}>
                  Næsta
                </Button>
              </div>
            )}
          </>
        )}
      </main>

      <Modal open={!!revokeTarget} onClose={() => setRevokeTarget(null)} title="Afturkalla kort">
        <p className="text-sm text-gray-600 mb-4">
          Ertu viss um að þú viljir afturkalla kort <strong>{revokeTarget?.userName}</strong> ({PASS_LABELS_IS[revokeTarget?.passType ?? 'single']})?
        </p>
        <div className="flex gap-2 justify-end">
          <Button variant="outline" onClick={() => setRevokeTarget(null)}>Hætta við</Button>
          <Button variant="danger" loading={actionLoading} onClick={handleRevoke}>Afturkalla</Button>
        </div>
      </Modal>
    </div>
  );
}
